import * as vp from 'vopay';
import { jsPDF } from 'jspdf';

const useFileDownload = (): {
  downloadJson: (data: unknown, fileName: string) => void;
  downloadPdf: (data: string, fileName: string) => void;
  downloadCsv: (data: string, fileName: string) => void;
  downloadCsvFile: (data: string, fileName: string) => void;
  printFile: (id: string) => void;
  downloadPdfPage: (id: string, fileName: string) => void;
} => {
  const downloadJson = (data: unknown, fileName: string) => {
    vp.downloadBlob({
      data: JSON.stringify(data, null, 4),
      fileName: `${fileName.replace('.json', '')}.json`,
      fileType: 'text/json',
    });
  };

  const downloadPdf = (data: string, fileName: string) => {
    vp.downloadBase64DataUri({
      data,
      fileName: `${fileName.replace('.pdf', '')}.pdf`,
      fileType: 'application/pdf',
    });
  };

  const downloadCsv = (data: string, fileName: string) => {
    vp.downloadBase64DataUri({
      data,
      fileName: `${fileName.replace('.csv', '')}.csv`,
      fileType: 'application/octet-stream',
    });
  };

  const downloadCsvFile = (data: string, fileName: string) => {
    const downloadLink = document.createElement('a');
    downloadLink.href = data;
    downloadLink.setAttribute('download', `${fileName.replace('.csv', '')}.csv`);
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const downloadPdfPage = (id: string, fileName: string) => {
    const doc = new jsPDF();

    const div = document.getElementById(id);
    const hiddenElements = div?.getElementsByClassName('hiddenFormsPrint');
    const gradientElements = div?.getElementsByClassName('pdfSolidBg');
    const img = div?.getElementsByTagName('img');

    if (hiddenElements) {
      for (const element of hiddenElements) {
        const currentElement = element as HTMLElement;
        currentElement.style.display = 'none';
      }
    }

    if (gradientElements) {
      for (const element of gradientElements) {
        const currentElement = element as HTMLElement;
        currentElement.style.background = currentElement.dataset.pdfBg ?? 'transparent';
      }
    }

    if (img) {
      for (const image of img) {
        const currentImage = image as HTMLImageElement;
        currentImage.style.display = 'none';
      }
    }

    doc.html(String(div?.outerHTML), {
      callback(doc) {
        const tableCallBack = document.getElementById(id);
        const hiddenElementsCallBack = tableCallBack?.getElementsByClassName('hiddenFormsPrint');
        const gradientElementsCallBack = tableCallBack?.getElementsByClassName('pdfSolidBg');
        const img = tableCallBack?.getElementsByTagName('img');
        doc.save(`${fileName}.pdf`);

        if (hiddenElementsCallBack) {
          for (const element of hiddenElementsCallBack) {
            const currentElement = element as HTMLElement;
            currentElement.style.display = 'initial';
          }
        }

        if (gradientElementsCallBack) {
          for (const element of gradientElementsCallBack) {
            const currentElement = element as HTMLElement;
            currentElement.style.background = '';
          }
        }

        if (img) {
          for (const image of img) {
            const currentImage = image as HTMLImageElement;
            currentImage.style.display = 'initial';
          }
        }
      },
      margin: [10, 10, 10, 10],
      autoPaging: 'text',
      x: 0,
      y: 0,
      width: 150,
      windowWidth: 675,
    });
  };

  const getStyleSheets = () => {
    const styleSheets = Array.from(document.styleSheets);

    const styles = styleSheets.map((styleSheet) => {
      if (styleSheet.href) {
        // External stylesheets
        return styleSheet.href;
      }

      // Inline styles
      const rules = Array.from(styleSheet.cssRules).map((rule) => rule.cssText);

      return rules.join('\n');
    });

    return styles.join('\n');
  };

  function isURL(str: string) {
    const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;

    return urlPattern.test(str);
  }

  const printFile = (id: string) => {
    const element = document.getElementById(id);
    if (!element) return;

    const overlay = document.createElement('div');
    overlay.id = 'printOverlay';

    Object.assign(overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      background: 'rgba(0,0,0,0.4)',
      zIndex: '9999',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: '18px',
      fontFamily: 'sans-serif',
    });

    overlay.textContent = 'Preparing print view...';
    document.body.appendChild(overlay);

    const styles = getStyleSheets();
    const isExternal = isURL(styles);

    const newWindow = window.open('', '_blank');
    if (!newWindow) return;

    // Skeleton only carries the page's own stylesheets (never user/backend content) - the actual
    // content is appended below via cloneNode, not string interpolation, to avoid re-parsing HTML.
    const html = `
    <html>
      <head>
        ${isExternal ? `<link rel="stylesheet" href="${styles}">` : `<style>${styles}</style>`}
        <style>.hiddenFormsPrint { display: none; }</style>
      </head>
      <body></body>
    </html>
  `;

    newWindow.document.write(html);
    newWindow.document.close();
    newWindow.document.body.appendChild(element.cloneNode(true));

    newWindow.onload = () => {
      newWindow.print();

      newWindow.onafterprint = () => {
        newWindow.close();
        document.getElementById('printOverlay')?.remove();
      };

      setTimeout(() => {
        newWindow.close();
        document.getElementById('printOverlay')?.remove();
      }, 3000);
    };
  };

  return {
    downloadJson,
    downloadPdf,
    downloadCsv,
    downloadCsvFile,
    printFile,
    downloadPdfPage,
  };
};

export default useFileDownload;
